import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { checkImport, importCsv, fetchFile, fetchFiles } from "../../../services/aro/importService";
import { fetchEvents } from "../../../services/aro/eventService";
import { fetchCertificateTypes } from "../../../services/aro/certificateService";
import { AroApiError } from "../../../services/aroApi";
import {
  ImportResponse, FileRecord, AROEvent, ImportCheckResult, ImportCertificateGroup, ImportEventGroup,
  ImportRejectedDetail, CertificateAnswer, EventAnswer,
} from "../../../types/aro";
import { useAuth } from "../../../context/useAuth";
import { canAccessQuarantine, canViewImportHistory } from "../../../utils/quarantineAccess";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import { formatDateLong, formatLabel, formatLevel } from "../../../utils/formatters";
import "./ImportPage.css";

type ActiveView = "upload" | "history";

interface CsvPreview { headers: string[]; rows: string[][]; }

// "type:<id>" = an existing certificate type, "new" = create one (level required)
interface CertDraft  { choice: string; level: string; }
// "event:<id>" = an existing event, "new" = create one, "none" = no event
interface EventDraft { choice: string; event_name: string; start_date: string; end_date: string; delivery: string; }

const REQUIRED_COLS = ["Credential ID", "Email", "Certificate", "Issued on"];
const OPTIONAL_COLS = ["Name", "Event / cohort", "Event date", "Delivery", "Organization", "LinkedIn", "How issued", "Credential URL"];
const ALLOWED_EXTS  = [".csv", ".xlsx"];
const NEW_ISSUER    = "__new__";


function parseCsvPreview(text: string): CsvPreview {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const parse = (line: string) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  return { headers: parse(lines[0]), rows: lines.slice(1, 6).map(parse) };
}

function fileExt(name: string) { return name.slice(name.lastIndexOf(".")).toLowerCase(); }

function eventRange(start: string | null, end: string | null): string {
  if (!start) return "no date";
  return end && end !== start ? `${formatDateLong(start)} – ${formatDateLong(end)}` : formatDateLong(start);
}

/** How close a file's event date is to an existing event's range — "dates overlap" or "N days apart". */
function dateCloseness(fileDate: string | null, start: string, end: string | null): string {
  if (!fileDate) return "";
  const day = 86_400_000;
  const d = Date.parse(fileDate), s = Date.parse(start), e = Date.parse(end || start);
  if (d >= s && d <= e) return "dates overlap";
  const n = Math.round(Math.min(Math.abs(d - s), Math.abs(d - e)) / day);
  return `${n} day${n !== 1 ? "s" : ""} apart`;
}

function initialCertDraft(g: ImportCertificateGroup): CertDraft {
  return { choice: g.match === "new" ? "new" : "", level: "" };
}

function initialEventDraft(g: ImportEventGroup): EventDraft {
  const choice =
    g.match === "exact" ? `event:${g.candidates[0].id}` :
    g.match === "none"  ? "none" :
    g.match === "new"   ? "new" : "";
  return {
    choice,
    event_name: g.event_name ?? "",
    start_date: g.start_date ?? "",
    end_date:   g.end_date ?? g.start_date ?? "",   // single-day events default to the start date
    delivery:   g.delivery ?? "",
  };
}

function certAnswer(d: CertDraft, g: ImportCertificateGroup): CertificateAnswer | null {
  if (d.choice.startsWith("type:")) return { certificate_type_id: Number(d.choice.slice(5)) };
  if (d.choice === "new" && d.level) {
    return { new: { certificate_name: g.name, level: d.level === "external" ? null : Number(d.level) } };
  }
  return null;
}

function eventAnswer(d: EventDraft): EventAnswer | null {
  if (d.choice.startsWith("event:")) return { event_id: Number(d.choice.slice(6)) };
  if (d.choice === "none") return { none: true };
  // New events need a name, a start date and an end date on/after the start
  if (d.choice === "new" && d.event_name.trim() && d.start_date && d.end_date && d.end_date >= d.start_date) {
    return { new: {
      event_name: d.event_name.trim(),
      start_date: d.start_date,
      end_date:   d.end_date,
      delivery:   d.delivery.trim() || null,
    } };
  }
  return null;
}

function StatusPill({ status }: { status: FileRecord["status"] }) {
  return <span className={`aro-status-pill aro-status-pill--${status}`}>{status}</span>;
}

export default function ImportPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { email: uploadedBy } = useAuth();
  const canViewHistory = canViewImportHistory(uploadedBy);

  // ── Form state ────────────────────────────────────────────
  const [file,           setFile]           = useState<File | null>(null);
  const [preview,        setPreview]        = useState<CsvPreview | null>(null);
  const [issuers,        setIssuers]        = useState<string[]>([]);
  const [issuerChoice,   setIssuerChoice]   = useState("");
  const [newIssuer,      setNewIssuer]      = useState("");
  const [events,         setEvents]         = useState<AROEvent[]>([]);
  const [formError,      setFormError]      = useState<string | null>(null);

  // ── Check (dry run) state ─────────────────────────────────
  const [check,          setCheck]          = useState<ImportCheckResult | null>(null);
  const [checking,       setChecking]       = useState(false);
  const [missingCols,    setMissingCols]    = useState<string[] | null>(null);
  const [certDrafts,     setCertDrafts]     = useState<Record<string, CertDraft>>({});
  const [eventDrafts,    setEventDrafts]    = useState<Record<string, EventDraft>>({});
  const [expandIssues,   setExpandIssues]   = useState(false);

  // ── Submission / polling state ────────────────────────────
  const [submitting,   setSubmitting]   = useState(false);
  const [startResult,  setStartResult]  = useState<ImportResponse | null>(null);
  const [polledFile,   setPolledFile]   = useState<FileRecord | null>(null);
  const [polling,      setPolling]      = useState(false);
  const [expandErrors, setExpandErrors] = useState(false);

  // ── History state ─────────────────────────────────────────
  const [activeView,          setActiveView]          = useState<ActiveView>("upload");
  const [historyFiles,        setHistoryFiles]        = useState<FileRecord[]>([]);
  const [historyLoading,      setHistoryLoading]      = useState(false);
  const [historyError,        setHistoryError]        = useState<string | null>(null);
  const [selectedHistoryId,   setSelectedHistoryId]   = useState<string | null>(null);
  const [historyDetail,       setHistoryDetail]       = useState<FileRecord | null>(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [historyExpandErrors, setHistoryExpandErrors] = useState(false);

  const issuerName = (issuerChoice === NEW_ISSUER ? newIssuer : issuerChoice).trim();

  // ── Load history, events & issuers ────────────────────────
  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    setHistoryError(null);
    fetchFiles({ limit: 50 })
      .then((r) => setHistoryFiles(r.files))
      .catch((e) => setHistoryError(e.message))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => {
    loadHistory();
    fetchEvents().then((r) => setEvents(r.events)).catch(() => {});
    fetchCertificateTypes()
      .then((r) => {
        setIssuers(r.issuers);
        if (r.issuers.length > 0) setIssuerChoice((c) => c || r.issuers[0]);
      })
      .catch(() => {});
  }, [loadHistory]);

  // ── Check: runs whenever the file or issuer changes ───────
  useEffect(() => {
    setCheck(null);
    setMissingCols(null);
    if (!file || !issuerName || startResult) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setChecking(true);
      setFormError(null);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("issuer_company_name", issuerName);
      try {
        const result = await checkImport(fd);
        if (cancelled) return;
        setCheck(result);
        setCertDrafts(Object.fromEntries(result.certificates.map((g) => [g.key, initialCertDraft(g)])));
        setEventDrafts(Object.fromEntries(result.events.map((g) => [g.key, initialEventDraft(g)])));
      } catch (e: any) {
        if (cancelled) return;
        const detail = e instanceof AroApiError ? (e.detail as ImportRejectedDetail) : null;
        if (detail?.missing_columns) setMissingCols(detail.missing_columns);
        else setFormError(e.message);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [file, issuerName, startResult]);

  // ── Polling ───────────────────────────────────────────────
  useEffect(() => {
    if (!polling || !startResult?.file_id) return;
    const interval = setInterval(async () => {
      try {
        const f = await fetchFile(startResult.file_id);
        setPolledFile(f);
        if (f.status === "complete" || f.status === "failed") {
          setPolling(false);
          loadHistory();
        }
      } catch {
        // ignore transient errors, keep polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, startResult, loadHistory]);

  // ── File handling ─────────────────────────────────────────
  const applyFile = (f: File) => {
    if (!ALLOWED_EXTS.includes(fileExt(f.name))) {
      setFormError("Only .csv or .xlsx files are supported.");
      return;
    }
    setFile(f);
    setFormError(null);
    setStartResult(null);
    setPolledFile(null);
    setExpandErrors(false);
    if (fileExt(f.name) === ".csv") {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(parseCsvPreview(ev.target?.result as string));
      reader.readAsText(f);
    } else {
      setPreview(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) applyFile(f); else { setFile(null); setPreview(null); }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) applyFile(f);
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
    setFormError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const updateCertDraft  = (key: string, patch: Partial<CertDraft>) =>
    setCertDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  const updateEventDraft = (key: string, patch: Partial<EventDraft>) =>
    setEventDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  // ── Answers: every non-exact certificate / event needs one ─
  const certNeedsAnswer  = (g: ImportCertificateGroup) => g.match !== "exact" && !certAnswer(certDrafts[g.key], g);
  const eventNeedsAnswer = (g: ImportEventGroup)       => g.match !== "exact" && !eventAnswer(eventDrafts[g.key]);
  const unanswered = check
    ? check.certificates.filter(certNeedsAnswer).length + check.events.filter(eventNeedsAnswer).length
    : 0;
  const newEventCount = check ? check.events.filter((g) => eventDrafts[g.key]?.choice === "new").length : 0;

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!file || !check || unanswered > 0) return;

    const certificateMapping: Record<string, CertificateAnswer> = {};
    check.certificates.forEach((g) => {
      const answer = g.match === "exact" ? null : certAnswer(certDrafts[g.key], g);
      if (answer) certificateMapping[g.key] = answer;
    });
    const eventMapping: Record<string, EventAnswer> = {};
    check.events.forEach((g) => {
      const answer = g.match === "exact" ? null : eventAnswer(eventDrafts[g.key]);
      if (answer) eventMapping[g.key] = answer;
    });

    setSubmitting(true);
    setFormError(null);
    setPolledFile(null);
    setExpandErrors(false);

    const fd = new FormData();
    fd.append("file",                file);
    fd.append("uploaded_by",         uploadedBy.trim());
    fd.append("issuer_company_name", issuerName);
    fd.append("certificate_mapping", JSON.stringify(certificateMapping));
    fd.append("event_mapping",       JSON.stringify(eventMapping));

    try {
      const res = await importCsv(fd);
      setStartResult(res);
      setPolling(true);
    } catch (e: any) {
      const detail = e instanceof AroApiError ? (e.detail as ImportRejectedDetail) : null;
      setFormError(detail?.errors?.length ? `${detail.message} ${detail.errors.join("; ")}` : e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFile(null); setPreview(null); setStartResult(null); setCheck(null); setMissingCols(null);
    setPolledFile(null); setFormError(null); setPolling(false); setExpandErrors(false); setExpandIssues(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── History row click ─────────────────────────────────────
  const handleHistoryRowClick = async (f: FileRecord) => {
    if (selectedHistoryId === f.file_id) {
      setSelectedHistoryId(null);
      setHistoryDetail(null);
      setHistoryExpandErrors(false);
      return;
    }
    setSelectedHistoryId(f.file_id);
    setHistoryDetail(null);
    setHistoryExpandErrors(false);
    setHistoryDetailLoading(true);
    try {
      setHistoryDetail(await fetchFile(f.file_id));
    } catch {
      setHistoryDetail(f);
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  const isDone = polledFile?.status === "complete" || polledFile?.status === "failed";
  const rowIssues = check ? [...check.rows.quarantine, ...check.rows.warnings] : [];

  return (
    <div className="aro-page">
      <div className="aro-import-header">
        <h1 className="aro-import-title">Import Certificates</h1>
        <p className="aro-import-subtitle">Upload a CSV or XLSX file to bulk-add certificate earners.</p>
      </div>

      {/* View tabs */}
      <div className="aro-tabs">
        <button
          className={`aro-tab ${activeView === "upload" ? "aro-tab--active" : ""}`}
          onClick={() => setActiveView("upload")}
        >
          Upload
        </button>
        {canViewHistory && (
          <button
            className={`aro-tab ${activeView === "history" ? "aro-tab--active" : ""}`}
            onClick={() => { setActiveView("history"); loadHistory(); }}
          >
            Upload History
          </button>
        )}
      </div>

      {/* ── Upload view ──────────────────────────────────────── */}
      {activeView === "upload" && (
        <>
          <div className="aro-import-body">

            {/* Drag-drop zone */}
            <div
              className={`aro-dropzone ${file ? "aro-dropzone--has-file" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => !file && !startResult && fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {file ? (
                <span className="aro-dropzone__file-row">
                  <span className="aro-dropzone__filename">📄 {file.name}</span>
                  <button
                    className="aro-dropzone__remove"
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                    title="Remove file"
                  >
                    ✕
                  </button>
                </span>
              ) : (
                <>
                  <span className="aro-dropzone__icon">⬆</span>
                  <span className="aro-dropzone__text">Drag &amp; drop a CSV or XLSX file here, or click to browse</span>
                </>
              )}
            </div>

            {/* Missing columns — file rejected */}
            {missingCols && (
              <div className="aro-warn-banner">
                ⚠ Your file is missing required columns: <strong>{missingCols.join(", ")}</strong>. Add them and upload again.
              </div>
            )}

            {/* CSV preview */}
            {file && fileExt(file.name) === ".xlsx" && (
              <div className="aro-preview">
                <p className="aro-preview__label">Preview not available for Excel files — your file will be processed on upload.</p>
              </div>
            )}
            {preview && preview.headers.length > 0 && (
              <div className="aro-preview">
                <p className="aro-preview__label">Preview (first 5 rows)</p>
                <div className="aro-preview__scroll">
                  <table className="aro-table aro-preview__table">
                    <thead>
                      <tr>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i} className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                          {row.map((cell, j) => <td key={j}>{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Form fields */}
            <div className="aro-import-fields">
              <div className="aro-import-field">
                <label className="aro-import-label">Uploaded By</label>
                <input
                  className="aro-input"
                  value={uploadedBy}
                  readOnly
                  style={{ background: "var(--color-bg-secondary)", cursor: "default" }}
                />
              </div>

              <div className="aro-import-field">
                <label className="aro-import-label aro-import-label--required">Issuer</label>
                <select
                  className="aro-select"
                  value={issuerChoice}
                  onChange={(e) => setIssuerChoice(e.target.value)}
                >
                  {issuers.map((name) => <option key={name} value={name}>{name}</option>)}
                  <option value={NEW_ISSUER}>New issuer…</option>
                </select>
                {issuerChoice === NEW_ISSUER && (
                  <input
                    className="aro-input"
                    placeholder="Issuer company name"
                    value={newIssuer}
                    onChange={(e) => setNewIssuer(e.target.value)}
                  />
                )}
              </div>
            </div>

            {checking && (
              <div className="aro-import-polling">
                <span className="aro-import-polling__spinner" />
                <span>Checking <strong>{file?.name}</strong>…</span>
              </div>
            )}

            {/* ── Check results ─────────────────────────────── */}
            {check && !startResult && (
              <>
                {check.issuer.match === "close" && issuerChoice === NEW_ISSUER && (
                  <div className="aro-import-help aro-import-help--nudge">
                    Is this <strong>{check.issuer.candidates[0]}</strong>?{" "}
                    <button className="aro-link-btn" onClick={() => setIssuerChoice(check.issuer.candidates[0])}>
                      Yes, use {check.issuer.candidates[0]}
                    </button>
                    {" "}— or keep typing to create a new issuer.
                  </div>
                )}
                {check.issuer.match === "new" && (
                  <p className="aro-import-help">
                    New issuer <strong>{issuerName}</strong> — every certificate in this file will be created as a new certificate type for it.
                  </p>
                )}

                {/* Certificates */}
                <div className="aro-preview">
                  <p className="aro-preview__label">Certificates in this file</p>
                  <div className="aro-preview__scroll">
                    <table className="aro-table">
                      <thead>
                        <tr>
                          <th>Certificate in file</th>
                          <th>Rows</th>
                          <th>Matches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {check.certificates.map((g, i) => {
                          const draft = certDrafts[g.key];
                          return (
                            <tr key={g.key} className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                              <td className="aro-table__cell--primary">{g.name}</td>
                              <td>{g.row_count}</td>
                              <td>
                                {g.match === "exact" ? (
                                  <span>✓ {g.candidates[0].certificate_name} ({formatLevel(g.candidates[0].level)})</span>
                                ) : (
                                  <>
                                    {g.match === "close" && (
                                      <select
                                        className="aro-select"
                                        value={draft?.choice ?? ""}
                                        onChange={(e) => updateCertDraft(g.key, { choice: e.target.value })}
                                      >
                                        <option value="">Is this an existing certificate? Choose…</option>
                                        {g.candidates.map((t) => (
                                          <option key={t.id} value={`type:${t.id}`}>
                                            Yes — {t.certificate_name} ({formatLevel(t.level)})
                                          </option>
                                        ))}
                                        <option value="new">No, it's a new certificate</option>
                                      </select>
                                    )}
                                    {draft?.choice === "new" && (
                                      <select
                                        className="aro-select"
                                        value={draft.level}
                                        onChange={(e) => updateCertDraft(g.key, { level: e.target.value })}
                                      >
                                        <option value="">New certificate — choose its level…</option>
                                        <option value="1">Level 1</option>
                                        <option value="2">Level 2</option>
                                        <option value="3">Level 3</option>
                                        <option value="external">External</option>
                                      </select>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Events */}
                <div className="aro-preview">
                  <p className="aro-preview__label">Events in this file</p>
                  <div className="aro-preview__scroll">
                    <table className="aro-table">
                      <thead>
                        <tr>
                          <th>Event in file</th>
                          <th>Date</th>
                          <th>Rows</th>
                          <th>Matches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {check.events.map((g, i) => {
                          const draft = eventDrafts[g.key];
                          const suggestedIds = new Set(g.candidates.map((c) => c.id));
                          return (
                            <React.Fragment key={g.key}>
                              <tr className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                                <td className="aro-table__cell--primary">
                                  {g.key === "__all__" ? "All rows (file has no event columns)" : g.event_name ?? "(no event)"}
                                </td>
                                <td>{g.key === "__all__" ? "—" : eventRange(g.start_date, null)}</td>
                                <td>{g.row_count}</td>
                                <td>
                                  {g.match === "exact" ? (
                                    <span>✓ {g.candidates[0].event_name} ({eventRange(g.candidates[0].start_date, g.candidates[0].end_date)})</span>
                                  ) : (
                                    <>
                                      <select
                                        className="aro-select"
                                        value={draft?.choice ?? ""}
                                        onChange={(e) => updateEventDraft(g.key, { choice: e.target.value })}
                                      >
                                        <option value="">
                                          {g.match === "close" ? "Is this an existing event? Choose…" : "Choose…"}
                                        </option>
                                        {g.candidates.length > 0 && (
                                          <optgroup label="Suggested — likely the same event">
                                            {g.candidates.map((c) => (
                                              <option key={c.id} value={`event:${c.id}`}>
                                                Same as: {c.event_name} · {eventRange(c.start_date, c.end_date)}
                                                {g.key !== "__all__" && dateCloseness(g.start_date, c.start_date, c.end_date)
                                                  ? ` · ${dateCloseness(g.start_date, c.start_date, c.end_date)}` : ""}
                                              </option>
                                            ))}
                                          </optgroup>
                                        )}
                                        <optgroup label="Or">
                                          <option value="new">{g.match === "close" ? "No — create it as a new event" : "Create it as a new event"}</option>
                                          <option value="none">No event for these rows</option>
                                        </optgroup>
                                        {events.some((ev) => !suggestedIds.has(ev.id)) && (
                                          <optgroup label="Other existing events">
                                            {events.filter((ev) => !suggestedIds.has(ev.id)).map((ev) => (
                                              <option key={ev.id} value={`event:${ev.id}`}>
                                                {ev.event_name} · {eventRange(ev.start_date, ev.end_date)}
                                              </option>
                                            ))}
                                          </optgroup>
                                        )}
                                      </select>
                                      {!draft?.choice && g.candidates.length > 0 && (
                                        <div className="aro-muted">
                                          Closest: {g.candidates[0].event_name} ({eventRange(g.candidates[0].start_date, g.candidates[0].end_date)}
                                          {dateCloseness(g.start_date, g.candidates[0].start_date, g.candidates[0].end_date)
                                            ? `, ${dateCloseness(g.start_date, g.candidates[0].start_date, g.candidates[0].end_date)}` : ""})
                                        </div>
                                      )}
                                    </>
                                  )}
                                </td>
                              </tr>
                              {draft?.choice === "new" && g.match !== "exact" && (
                                <tr>
                                  <td colSpan={4}>
                                    <div className="aro-import-fields">
                                      <div className="aro-import-field">
                                        <label className="aro-import-label aro-import-label--required">New event name</label>
                                        <input className="aro-input" value={draft.event_name}
                                          onChange={(e) => updateEventDraft(g.key, { event_name: e.target.value })} />
                                      </div>
                                      <div className="aro-import-field">
                                        <label className="aro-import-label aro-import-label--required">Start date</label>
                                        <input className="aro-input" type="date" value={draft.start_date}
                                          onChange={(e) => updateEventDraft(g.key, { start_date: e.target.value })} />
                                      </div>
                                      <div className="aro-import-field">
                                        <label className="aro-import-label aro-import-label--required">End date</label>
                                        <input className="aro-input" type="date" value={draft.end_date} min={draft.start_date || undefined}
                                          onChange={(e) => updateEventDraft(g.key, { end_date: e.target.value })} />
                                        {draft.end_date && draft.start_date && draft.end_date < draft.start_date && (
                                          <span className="aro-muted">End date must be on or after the start date.</span>
                                        )}
                                      </div>
                                      <div className="aro-import-field">
                                        <label className="aro-import-label">Delivery</label>
                                        <input className="aro-input" value={draft.delivery} placeholder="conference, workshop, virtual…"
                                          onChange={(e) => updateEventDraft(g.key, { delivery: e.target.value })} />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Row problems */}
                <div className="aro-import-result__stats">
                  <span>Rows to import: <strong>{check.rows.to_import}</strong></span>
                  <span>Already imported (skipped): <strong>{check.rows.already_imported}</strong></span>
                  <span>To quarantine: <strong>{check.rows.quarantine.length}</strong></span>
                  <span>Warnings: <strong>{check.rows.warnings.length}</strong></span>
                </div>
                {rowIssues.length > 0 && (
                  <div className="aro-import-result__errors">
                    <button className="aro-import-result__expand-btn" onClick={() => setExpandIssues((v) => !v)}>
                      {expandIssues ? "▲ Hide" : "▼ Show"} {rowIssues.length} row message{rowIssues.length !== 1 ? "s" : ""}
                    </button>
                    {expandIssues && (
                      <ul className="aro-import-result__error-list">
                        {rowIssues.map((r, i) => (
                          <li key={i}>
                            Row {r.row_number} ({r.email || "no email"}, {r.credential_id || "no credential ID"}): {formatLabel(r.reason)}
                            {r.detail ? ` — ${r.detail}` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {check.rows.quarantine.length > 0 && (
                  <p className="aro-import-help">
                    Rows with problems go to Quarantine — you can fix them there after importing, or fix the file now.
                  </p>
                )}
              </>
            )}

            {formError && <div className="aro-error-banner">{formError}</div>}

            {/* Polling / result state */}
            {startResult && !isDone && (
              <div className="aro-import-polling">
                <span className="aro-import-polling__spinner" />
                <span>
                  Import started — tracking <strong>{startResult.filename}</strong>…
                </span>
              </div>
            )}

            {isDone && polledFile && (
              <div className={`aro-import-result aro-import-result--${polledFile.status}`}>
                {polledFile.status === "complete" ? (
                  <>
                    <p className="aro-import-result__title">✓ Import complete</p>
                    <div className="aro-import-result__stats">
                      <span>Rows processed: <strong>{polledFile.total_rows ?? "—"}</strong></span>
                      <span>Inserted: <strong>{polledFile.inserted ?? 0}</strong></span>
                      <span>Skipped (already imported / duplicates): <strong>{polledFile.skipped ?? 0}</strong></span>
                      <span>Quarantined: <strong>{polledFile.error_count ?? 0}</strong></span>
                    </div>
                    {(polledFile.error_details?.length ?? 0) > 0 && (
                      <div className="aro-import-result__errors">
                        <button
                          className="aro-import-result__expand-btn"
                          onClick={() => setExpandErrors((v) => !v)}
                        >
                          {expandErrors ? "▲ Hide" : "▼ Show"} {polledFile.error_details.length} message{polledFile.error_details.length !== 1 ? "s" : ""}
                        </button>
                        {expandErrors && (
                          <ul className="aro-import-result__error-list">
                            {polledFile.error_details.map((msg, i) => <li key={i}>{msg}</li>)}
                          </ul>
                        )}
                      </div>
                    )}
                    {(polledFile.error_count ?? 0) > 0 && canAccessQuarantine(uploadedBy) && (
                      <button
                        className="aro-link-btn"
                        onClick={() => navigate(`/ai-ready-ohio/quarantine?file_id=${polledFile.file_id}`)}
                      >
                        View Quarantined Rows →
                      </button>
                    )}
                    <button className="aro-btn aro-btn--ghost aro-import-result__reset" onClick={resetForm}>
                      Import another file
                    </button>
                  </>
                ) : (
                  <>
                    <p className="aro-import-result__title">✕ Import failed</p>
                    <p className="aro-import-result__msg">
                      {polledFile.error_details?.[0] ?? "An unknown error occurred."}
                    </p>
                    <button className="aro-btn aro-btn--ghost aro-import-result__reset" onClick={resetForm}>
                      Try again
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Submit button — hidden once import is started */}
            {!startResult && (
              <div className="aro-import-actions">
                <button
                  className="aro-btn aro-btn--primary"
                  onClick={handleSubmit}
                  disabled={submitting || checking || !check || unanswered > 0}
                >
                  {submitting
                    ? "Uploading…"
                    : check
                      ? `Import ${check.rows.to_import} rows` +
                        (check.rows.quarantine.length ? ` (${check.rows.quarantine.length} to quarantine)` : "") +
                        (newEventCount ? ` · creates ${newEventCount} new event${newEventCount !== 1 ? "s" : ""}` : "")
                      : "Submit Import"}
                </button>
                {check && unanswered > 0 && (
                  <span className="aro-muted">{unanswered} certificate/event question{unanswered !== 1 ? "s" : ""} still need an answer.</span>
                )}
              </div>
            )}
          </div>

          {/* Column reference */}
          <div className="aro-ref-card">
            <h3 className="aro-ref-card__title">Column Reference</h3>
            <div className="aro-ref-card__cols">
              <div>
                <span className="aro-ref-card__label">Required</span>
                <div className="aro-ref-card__tags">
                  {REQUIRED_COLS.map((c) => (
                    <span key={c} className="aro-ref-tag aro-ref-tag--required">{c}</span>
                  ))}
                </div>
              </div>
              <div>
                <span className="aro-ref-card__label">Optional</span>
                <div className="aro-ref-card__tags">
                  {OPTIONAL_COLS.map((c) => (
                    <span key={c} className="aro-ref-tag">{c}</span>
                  ))}
                </div>
              </div>
            </div>
            <p className="aro-ref-card__tip">
              💡 LinkedIn URLs are optional but required for employment tracking. Ask attendees to provide them at registration.
            </p>
          </div>

        </>
      )}

      {/* ── History view ─────────────────────────────────────── */}
      {activeView === "history" && canViewHistory && (
        <div className="aro-card aro-card--no-pad">
          {historyLoading ? (
            <LoadingSpinner />
          ) : historyError ? (
            <div className="aro-error-banner" style={{ margin: "var(--space-4)" }}>{historyError}</div>
          ) : historyFiles.length === 0 ? (
            <EmptyState icon="📂" message="No uploads yet." />
          ) : (
            <>
              <table className="aro-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Uploaded By</th>
                    <th>Date</th>
                    <th>Issuer</th>
                    <th>Status</th>
                    <th>Inserted</th>
                    <th>Skipped</th>
                    <th>Quarantined</th>
                  </tr>
                </thead>
                <tbody>
                  {historyFiles.map((f, i) => (
                    <tr
                      key={f.file_id}
                      className={[
                        i % 2 === 1 ? "aro-table__row--odd" : "",
                        "aro-table__row--clickable",
                        selectedHistoryId === f.file_id ? "aro-history-row--selected" : "",
                      ].join(" ")}
                      onClick={() => handleHistoryRowClick(f)}
                    >
                      <td className="aro-table__cell--primary">{f.file_name}</td>
                      <td>{f.uploaded_by}</td>
                      <td>{formatDateLong(f.uploaded_at)}</td>
                      <td className="aro-muted">{f.issuer_company_name ?? "—"}</td>
                      <td><StatusPill status={f.status} /></td>
                      <td>{f.inserted ?? "—"}</td>
                      <td>{f.skipped ?? "—"}</td>
                      <td>{f.error_count ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Detail panel */}
              {selectedHistoryId && (
                <div className="aro-history-detail">
                  {historyDetailLoading ? (
                    <p className="aro-muted">Loading details…</p>
                  ) : historyDetail ? (
                    <>
                      <p className="aro-history-detail__title">{historyDetail.file_name}</p>
                      <div className="aro-import-result__stats">
                        <span>Total rows: <strong>{historyDetail.total_rows ?? "—"}</strong></span>
                        <span>Inserted: <strong>{historyDetail.inserted ?? "—"}</strong></span>
                        <span>Skipped: <strong>{historyDetail.skipped ?? "—"}</strong></span>
                        <span>Quarantined: <strong>{historyDetail.error_count ?? "—"}</strong></span>
                      </div>
                      {canAccessQuarantine(uploadedBy) && (
                        <button
                          className="aro-link-btn"
                          onClick={() => navigate(`/ai-ready-ohio/quarantine?file_id=${historyDetail.file_id}`)}
                        >
                          View Quarantined Rows →
                        </button>
                      )}
                      {(historyDetail.error_details?.length ?? 0) > 0 && (
                        <div className="aro-import-result__errors">
                          <button
                            className="aro-import-result__expand-btn"
                            onClick={() => setHistoryExpandErrors((v) => !v)}
                          >
                            {historyExpandErrors ? "▲ Hide" : "▼ Show"} {historyDetail.error_details.length} message{historyDetail.error_details.length !== 1 ? "s" : ""}
                          </button>
                          {historyExpandErrors && (
                            <ul className="aro-import-result__error-list">
                              {historyDetail.error_details.map((msg, i) => <li key={i}>{msg}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
